import React from "react";

import { cn } from "../utils/dashboard.utils";

const toneMap = {
  blue: {
    ring: "ring-blue-500/20",
    icon: "text-blue-600 dark:text-blue-400",
    chip: "bg-blue-500/10 text-blue-700 dark:text-blue-300",
    accent: "bg-gradient-to-r from-blue-500/30 to-cyan-500/20",
  },

  indigo: {
    ring: "ring-indigo-500/20",
    icon: "text-indigo-600 dark:text-indigo-400",
    chip: "bg-indigo-500/10 text-indigo-700 dark:text-indigo-300",
    accent:
      "bg-gradient-to-r from-indigo-500/30 to-violet-500/20",
  },

  emerald: {
    ring: "ring-emerald-500/20",
    icon: "text-emerald-600 dark:text-emerald-400",
    chip:
      "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
    accent:
      "bg-gradient-to-r from-emerald-500/30 to-lime-500/20",
  },

  amber: {
    ring: "ring-amber-500/20",
    icon: "text-amber-600 dark:text-amber-400",
    chip: "bg-amber-500/10 text-amber-800 dark:text-amber-300",
    accent:
      "bg-gradient-to-r from-amber-500/30 to-orange-500/20",
  },

  rose: {
    ring: "ring-rose-500/20",
    icon: "text-rose-600 dark:text-rose-400",
    chip: "bg-rose-500/10 text-rose-700 dark:text-rose-300",
    accent:
      "bg-gradient-to-r from-rose-500/30 to-pink-500/20",
  },

  slate: {
    ring: "ring-slate-500/20",
    icon: "text-slate-700 dark:text-slate-300",
    chip: "bg-slate-500/10 text-slate-700 dark:text-slate-300",
    accent:
      "bg-gradient-to-r from-slate-400/30 to-slate-200/10",
  },
};

const badgeToneMap = {
  emerald:
    "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  amber:
    "bg-amber-500/10 text-amber-800 dark:text-amber-300",
  rose:
    "bg-rose-500/10 text-rose-700 dark:text-rose-300",
  blue:
    "bg-blue-500/10 text-blue-700 dark:text-blue-300",
  slate:
    "bg-slate-500/10 text-slate-700 dark:text-slate-300",
};

export function DashboardCard({
  className,
  children,
}) {
  return (
    <div
      className={cn(
        "rounded-2xl border border-slate-200/70 bg-white/70 shadow-sm backdrop-blur",
        "dark:border-slate-800 dark:bg-slate-900/60",
        className,
      )}
    >
      {children}
    </div>
  );
}

export function SectionHeader({
  title,
  subtitle,
  right,
}) {
  return (
    <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <h2 className="text-lg font-semibold text-slate-900 dark:text-white sm:text-xl">
          {title}
        </h2>

        {subtitle ? (
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
            {subtitle}
          </p>
        ) : null}
      </div>

      {right ? <div>{right}</div> : null}
    </div>
  );
}

export function StatCard({
  label,
  value,
  icon: Icon,
  tone = "blue",
  sub,
  loading = false,
}) {
  const selectedTone = toneMap[tone] || toneMap.blue;

  return (
    <DashboardCard
      className={cn(
        "relative overflow-hidden ring-1 transition hover:shadow-md",
        selectedTone.ring,
      )}
    >
      <div
        className={cn(
          "absolute inset-x-0 top-0 h-1.5",
          selectedTone.accent,
        )}
      />

      <div className="p-4 sm:p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
              {label}
            </div>

            <div className="mt-2">
              {loading ? (
                <div className="h-8 w-24 animate-pulse rounded-lg bg-slate-200/70 dark:bg-slate-800" />
              ) : (
                <div className="text-3xl font-bold leading-none text-slate-900 dark:text-white">
                  {value}
                </div>
              )}
            </div>

            {sub ? (
              <div
                className={cn(
                  "mt-2 inline-flex items-center gap-2 rounded-full px-2.5 py-1 text-xs font-medium",
                  selectedTone.chip,
                )}
              >
                {sub}
              </div>
            ) : (
              <div className="mt-2 h-5" />
            )}
          </div>

          <div className="shrink-0 rounded-2xl border border-slate-200/60 bg-slate-50 p-3 dark:border-slate-800 dark:bg-slate-950/60">
            <Icon
              className={cn(
                "h-5 w-5",
                selectedTone.icon,
              )}
            />
          </div>
        </div>
      </div>
    </DashboardCard>
  );
}

export function StatusBadge({
  children,
  tone = "slate",
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold",
        badgeToneMap[tone] || badgeToneMap.slate,
      )}
    >
      {children}
    </span>
  );
}